/**
 * TaskNotificationCard - Notification card for Task-type notifications
 *
 * Matches Xamarin NotificationListMobile.xaml task notification Frame:
 * - Left color bar: 5px wide, Move=#E38C25 (MoveTaskBackgroundColor), Audit=#273F94 (AuditTaskBackgroundColor)
 * - Row 0: Task type icon (move_small/audit_small) + "Task ID {id} - " + ActionCode text (colored by action)
 * - Row 1: Task description (Move: "Move Task : The trailer {name} from {src} to {dst}")
 *          (Audit with trailer: "Audit Task : Check if trailer {name} in {location}")
 *          (Audit without trailer: "Audit Task : Check if {location} is empty or not.")
 * - Row 2: Priority badge + "X ago" time label
 * - Background: new=#F2F5F8, read=white
 * - Border: #E0E7EE
 */

import { useTranslation } from 'react-i18next';

import { IMAGE_CONSTANTS, actionCodeToKey } from '@/constants';
import { cn } from '@/utils';

import type { NotificationModel } from '@/types';

interface TaskNotificationCardProps {
  notification: NotificationModel;
  onPress: (notification: NotificationModel) => void;
}

// Matches Xamarin ActionCode trigger colors
function getActionCodeColor(actionCode: string): string {
  const upper = actionCode.toUpperCase();
  if (upper === 'CREATE') return 'text-notification-bar-green';
  if (upper === 'DELETE') return 'text-notification-bar-red';
  if (upper === 'UPDATE') return 'text-notification-bar-orange';
  return 'text-notification-ago';
}

export function TaskNotificationCard({ notification, onPress }: TaskNotificationCardProps) {
  const { t } = useTranslation();
  const isMove = notification.taskType === 'Move';
  const barColor = isMove ? 'bg-move-task' : 'bg-audit-task';
  const typeIcon = isMove ? IMAGE_CONSTANTS.moveSmall : IMAGE_CONSTANTS.auditSmall;

  const handlePress = () => {
    onPress(notification);
  };

  return (
    <button
      onClick={handlePress}
      className={cn(
        'flex w-full overflow-hidden rounded border border-notification-border text-left',
        notification.isNew ? 'bg-notification-bg-new' : 'bg-white'
      )}
    >
      {/* Left color bar — Matches Xamarin BoxView WidthRequest=5, Margin="0,0,10,0" */}
      <div className={cn('w-[5px] shrink-0 self-stretch', barColor)} />

      {/* Content area — Matches Xamarin: BoxView right Margin=10 provides gap from bar */}
      <div className="flex flex-1 flex-col py-[10px] pl-[10px] pr-[10px]">
        {/* Row 0: Type icon + Task ID + Action code
            Matches Xamarin: Icon HeightRequest=25, TaskID PoppinsRegular FontSize=14(Phone),
            ActionCode PoppinsMedium FontSize=12(Phone) */}
        <div className="flex items-center gap-[5px]">
          <img
            src={typeIcon}
            alt={notification.taskType}
            className="h-[25px] w-[25px] object-contain"
          />
          <span className="font-poppins text-[15px] text-task-details">
            {t('notification.taskIdLabel')} {notification.taskId} -{' '}
          </span>
          <span
            className={cn(
              'font-poppins text-[12px] font-medium whitespace-nowrap',
              getActionCodeColor(notification.actionCode)
            )}
          >
            {t(actionCodeToKey(notification.actionCode))}
          </span>
        </div>

        {/* Row 1: Task description */}
        <div className="mt-[14px]">
          {isMove ? (
            <p className="font-poppins text-[15px] text-task-details">
              {t('notification.moveDescription')}{' '}
              <span className="font-bold">{notification.trailerName}</span> {t('notification.from')}{' '}
              <span className="font-bold">{notification.moveSourceName}</span>{' '}
              {t('notification.to')}{' '}
              <span className="font-bold">{notification.moveDestinationName}</span>
            </p>
          ) : notification.trailerName ? (
            <p className="font-poppins text-[14px] text-task-details">
              {t('notification.auditDescriptionWithTrailer')}
              <span className="font-bold">{notification.trailerName}</span> {t('notification.in')}{' '}
              <span className="font-bold">{notification.auditLocationName}</span>
            </p>
          ) : (
            <p className="font-poppins text-[15px] text-task-details">
              {t('notification.auditDescriptionWithoutTrailer')}
              <span className="font-bold">{notification.auditLocationName}</span>
              {t('notification.emptyOrNot')}
            </p>
          )}
        </div>

        {/* Row 2: Time ago — Matches Xamarin: Margin="0,0,5,0", FontSize=10, EndAndExpand */}
        <div className="mt-[8px] flex items-center justify-end">
          <span className="mr-[5px] font-poppins text-[14px] text-notification-ago whitespace-nowrap">
            {notification.updatedAgo}
          </span>
        </div>
      </div>
    </button>
  );
}
